import React, { useState, useRef, useEffect } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import './PDFViewer.css';

// Set up PDF.js worker - use local bundled worker
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.js';

function PDFViewer({ highlightBox, pdfPath, fileId, workOrderId }) {
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pdf, setPdf] = useState(null);
  const scale = 1;
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const textLayerRef = useRef(null);

  // Auto-navigate to the page with the highlighted field
  useEffect(() => {
    if (highlightBox && highlightBox.page && page !== highlightBox.page) {
      setPage(highlightBox.page);
    }
  }, [highlightBox, page]);

  useEffect(() => {
    if (!pdfPath && !(workOrderId && fileId)) return;

    const loadPdf = async () => {
      try {
        let url = null;

        // Try API endpoint if we have file ID and work order ID
        if (workOrderId && fileId) {
          url = `/api/work-orders/${workOrderId}/files/${fileId}/download`;
        } else if (pdfPath) {
          // Fallback to pdfPath
          url = pdfPath.replace('/InputPreprocess/', '/');
        }

        if (!url) return;

        const pdfDoc = await pdfjsLib.getDocument(url).promise;
        setPdf(pdfDoc);
        setTotalPages(pdfDoc.numPages);
        setPage(1);
      } catch (err) {
        console.error('Error loading PDF:', err);
      }
    };

    loadPdf();
  }, [pdfPath, fileId, workOrderId]);

  useEffect(() => {
    if (!pdf || !canvasRef.current) return;

    const renderPage = async () => {
      const pdfPage = await pdf.getPage(page);
      const viewport = pdfPage.getViewport({ scale });

      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const renderContext = {
        canvasContext: context,
        viewport: viewport
      };

      await pdfPage.render(renderContext).promise;

      // Draw highlight box if exists and is on current page
      if (highlightBox && (!highlightBox.page || highlightBox.page === page)) {
        const scaleFactor = scale;
        const x = highlightBox.x * scaleFactor;
        const y = highlightBox.y * scaleFactor;
        const w = highlightBox.width * scaleFactor;
        const h = highlightBox.height * scaleFactor;

        // Semi-transparent fill for visibility
        context.fillStyle = 'rgba(255, 200, 0, 0.2)';
        context.fillRect(x, y, w, h);

        // Bold border
        context.strokeStyle = '#ff9800';
        context.lineWidth = 3;
        context.shadowColor = 'rgba(0, 0, 0, 0.3)';
        context.shadowBlur = 3;
        context.shadowOffsetX = 1;
        context.shadowOffsetY = 1;
        context.strokeRect(x, y, w, h);
        context.shadowColor = 'transparent';

        // Draw label with better visibility
        context.fillStyle = '#ffffff';
        context.strokeStyle = '#ff9800';
        context.font = 'bold 12px Arial';
        context.lineWidth = 3;
        const textHeight = 16;
        const padding = 4;

        const labelX = Math.max(0, x);
        const labelY = Math.max(textHeight + padding, y - 5);

        // Background for label
        context.strokeText(highlightBox.text, labelX + padding, labelY - padding);
        context.fillStyle = '#ff9800';
        context.font = 'bold 12px Arial';
        context.fillText(highlightBox.text, labelX + padding, labelY - padding);

        // Confidence indicator if available
        if (highlightBox.confidence !== undefined) {
          context.fillStyle = 'rgba(0, 0, 0, 0.7)';
          context.font = '10px Arial';
          context.fillText(
            `Confidence: ${highlightBox.confidence}%`,
            x + padding,
            y + h + 15
          );
        }
      }
    };

    renderPage();
  }, [pdf, page, highlightBox, scale]);

  const handlePrevPage = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };

  return (
    <div className="pdf-viewer">
      <div className="pdf-header">
        <h3>PDF Preview</h3>
        <span className="page-info">Page {page} of {totalPages}</span>
      </div>

      <div className="pdf-canvas-container" ref={containerRef}>
        <canvas ref={canvasRef} className="pdf-canvas" />
        <div
          ref={textLayerRef}
          className="pdf-text-layer"
          draggable={true}
          onDragStart={(e) => {
            const selection = window.getSelection().toString();
            if (selection) {
              e.dataTransfer.effectAllowed = 'copy';
              e.dataTransfer.setData('text/plain', selection);
            }
          }}
        >
          {/* Text layer - will be populated dynamically */}
        </div>
        {highlightBox && (
          <div className="highlight-info">
            ✓ Field highlighted: <strong>{highlightBox.text}</strong>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pdf-controls">
          <button onClick={handlePrevPage} disabled={page === 1}>← Previous</button>
          <button onClick={handleNextPage} disabled={page === totalPages}>Next →</button>
        </div>
      )}
    </div>
  );
}

export default PDFViewer;
